// src/interfaces/manager/ManagerCalculatorNavigation.js
import React, { useState } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import PrintlogicCustomerModal from '../../components/manager/PrintlogicCustomerModal';

const API_URL = process.env.REACT_APP_API_URL || '';

const ManagerCalculatorNavigation = ({
  calculatorPage,
  setCalculatorPage,
  jobType,
  setJobType,
  pricingTier,
  setPricingTier,
  selectedCustomer,
  setSelectedCustomer
}) => {
  const { stopEditingDraft } = useQuotes();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // Handler to select customer and update backend session
  const handleSelectCustomer = async (customer) => {
    try {
      // Fetch custom pricing config (may be empty for customers without custom pricing)
      let hasCustomPricing = false;
      try {
        const configResponse = await fetch(
          `${API_URL}/api/customer/${customer.customer_id}/custom-pricing-config`,
          {
            credentials: 'include'
          }
        );

        if (configResponse.ok) {
          const customPricingConfig = await configResponse.json();
          hasCustomPricing = customPricingConfig.has_custom_pricing || false;
        }
      } catch (error) {
        console.log(`Could not fetch custom pricing config for customer ${customer.customer_id}`);
      }

      // Call backend to store customer_id in session
      // Allow ANY customer to be selected (they all have pricing_tier)
      const response = await fetch(`${API_URL}/api/manager/session/select-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: customer.customer_id,
          override_tier: customer.pricing_tier
        })
      });

      if (!response.ok) {
        console.error('Failed to select customer in session:', response.status);
        return;
      }

      // Update local state after successful backend update
      // Store both the customer AND whether they have custom pricing
      setSelectedCustomer({
        ...customer,
        _hasCustomPricing: hasCustomPricing
      });
      setPricingTier(customer.pricing_tier || 'retail');
      setShowCustomerModal(false);
    } catch (error) {
      console.error('Error selecting customer:', error);
    }
  };

  // Handler to clear customer selection and update backend session
  const handleClearCustomer = async () => {
    try {
      // Call backend to clear customer_id from session
      const response = await fetch(`${API_URL}/api/manager/session/clear-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Failed to clear customer in session:', response.status);
        return;
      }

      // Update local state after successful backend update
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error clearing customer:', error);
    }
  };

  return (
    <nav className="bg-white shadow">
      {/* Main navigation bar content */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-10">
          {/* Left side of nav: Logo and Desktop Links */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <span className="text-xl font-bold text-gray-800"></span>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <button
                onClick={() => {
                  stopEditingDraft();
                  setCalculatorPage('calculator');
                  setJobType('large-format');
                }}
                className={`${
                  calculatorPage === 'calculator' && jobType === 'large-format'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Large Format
              </button>
              <button
                onClick={() => {
                  stopEditingDraft();
                  setCalculatorPage('calculator');
                  setJobType('small-format');
                }}
                className={`${
                  calculatorPage === 'calculator' && jobType === 'small-format'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Small Format
              </button>
              {/* Drafts Tab */}
              <button
                onClick={() => setCalculatorPage('drafts')}
                className={`${
                  calculatorPage === 'drafts'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Drafts
              </button>
              <button
                onClick={() => setCalculatorPage('quotes')}
                className={`${
                  calculatorPage === 'quotes'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Quotes
              </button>
            </div>
          </div>

          {/* Mobile calculator tabs - visible only on mobile */}
          <div className="flex sm:hidden justify-center space-x-6 px-4 py-2 border-b">
            <button
              onClick={() => {
                stopEditingDraft();
                setCalculatorPage('calculator');
                setJobType('large-format');
              }}
              className={`${
                calculatorPage === 'calculator' && jobType === 'large-format'
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium`}
            >
              Large Format
            </button>
            <button
              onClick={() => {
                stopEditingDraft();
                setCalculatorPage('calculator');
                setJobType('small-format');
              }}
              className={`${
                calculatorPage === 'calculator' && jobType === 'small-format'
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium`}
            >
              Small Format
            </button>
          </div>

          {/* Right side of nav: Customer Selector and Pricing Tier Selector (visible for managers) */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
            {/* Customer Selector - Show when on calculator page */}
            {calculatorPage === 'calculator' && (
              <div className="relative">
                {selectedCustomer ? (
                  <div className="flex items-center gap-2 px-3 py-0.5 bg-blue-50 border border-blue-200 rounded">
                    <div className="font-medium text-blue-900 truncate max-w-xs text-sm">{selectedCustomer.customer_name}</div>
                    <button
                      onClick={handleClearCustomer}
                      className="text-xs text-gray-500 hover:text-gray-700 ml-1 flex-shrink-0"
                      title="Clear customer selection"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCustomerModal(true)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                  >
                    Customer Search 🔍
                  </button>
                )}
              </div>
            )}

            {/* Pricing Tier Dropdown - Show for managers on calculator page */}
            {calculatorPage === 'calculator' && (
              <div className="relative">
                <select
                  value={pricingTier}
                  onChange={(e) => setPricingTier(e.target.value)}
                  className={`block px-3 py-1 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    selectedCustomer && pricingTier === (selectedCustomer.pricing_tier || 'retail')
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="retail">Retail</option>
                  <option value="partner">Partner (-37.5%)</option>
                  <option value="choosyg">Choosy Giant (-15%)</option>
                  {/* Trade option hidden but logic preserved for future use */}
                  {/* <option value="trade">Trade (Legacy)</option> */}
                </select>
              </div>
            )}
          </div>

          {/* Mobile menu button (Hamburger) */}
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed. */}
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                // Icon when menu is open.
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state. */}
      {isMobileMenuOpen && (
        <div className="sm:hidden" id="mobile-menu">
          <div className="pt-2 pb-3 space-y-1">
            {/* Drafts Mobile Button */}
            <button
              onClick={() => {
                setCalculatorPage('drafts');
                setIsMobileMenuOpen(false);
              }}
              className={`${
                calculatorPage === 'drafts'
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left`}
            >
              Drafts
            </button>
            <button
              onClick={() => {
                setCalculatorPage('quotes');
                setIsMobileMenuOpen(false);
              }}
              className={`${
                calculatorPage === 'quotes'
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left`}
            >
              Quotes
            </button>

            {/* Customer Selector - Mobile (show for managers on calculator page) */}
            {calculatorPage === 'calculator' && (
              <div className="pl-3 pr-4 py-2 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                {selectedCustomer ? (
                  <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm flex-1 min-w-0">
                      <div className="font-medium text-blue-900 truncate">{selectedCustomer.customer_name}</div>
                      <div className="text-xs text-blue-700">
                        {selectedCustomer.pricing_tier === 'partner'
                          ? 'Partner'
                          : selectedCustomer.pricing_tier === 'trade'
                          ? 'Trade'
                          : 'Retail'}
                      </div>
                    </div>
                    <button
                      onClick={handleClearCustomer}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded ml-2"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setShowCustomerModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                  >
                    Select Customer
                  </button>
                )}
              </div>
            )}

            {/* Pricing Tier Selector - Mobile (show for managers on calculator page) */}
            {calculatorPage === 'calculator' && (
              <div className="pl-3 pr-4 py-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Tier</label>
                <select
                  value={pricingTier}
                  onChange={(e) => setPricingTier(e.target.value)}
                  className={`block w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    selectedCustomer && pricingTier === (selectedCustomer.pricing_tier || 'retail')
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="retail">Retail</option>
                  <option value="partner">Partner (-37.5%)</option>
                  <option value="choosyg">Choosy Giant (-15%)</option>
                  {/* Trade option hidden but logic preserved for future use */}
                  {/* <option value="trade">Trade (Legacy)</option> */}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Selection Modal */}
      <PrintlogicCustomerModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={handleSelectCustomer}
      />
    </nav>
  );
};

export default ManagerCalculatorNavigation;
